import React, { useEffect, useRef, useState } from "react";
import { TabPanel, TabView } from "primereact/tabview";
import type { Node } from "@xyflow/react";
import IVRSchemeBuilder, {
    type IVRSchemeBuilderRef,
    type IVRBlockData,
    type IVRBlockConfig,
    type IvrSchemePlaybackState,
    isIvrEndBlockData,
} from "./IVRSchemeBuilder";
import IVRSchemeNav from "./IVRSchemeNav";
import BlockSettingsPanel from "./BlockSettingsPanel";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { getAllQueues } from "../../../../redux/api/apiQueue";
import {useParams} from "react-router-dom";
import {getIVRConfigurationFullById} from "../../../../redux/api/apiIVRConfiguration";
import "./IVRSchemeSetting.scss";

const IVRSchemeSetting = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [activeSettingIndex, setActiveSettingIndex] = useState(0);
    const [selectedNode, setSelectedNode] = useState<
        Node<IVRBlockData> | null
    >(null);
    const schemeBuilderRef = useRef<IVRSchemeBuilderRef>(null);
    const [schemePlaybackState, setSchemePlaybackState] =
        useState<IvrSchemePlaybackState>({ active: false, paused: false });
    const { id } = useParams();
    const flowIdFromRoute =
        id != null && id !== "" && !Number.isNaN(Number(id))
            ? Number(id)
            : null;
    const dispatch = useAppDispatch();
    const jwtToken = useAppSelector(
        (state) => state.UsersReducer.userData.jwtToken
    );
    const activeClick = useAppSelector((state) => state.UsersListReducer.activeClick);

    const numericId = Number(id);
    const isValidId = Boolean(id) && !isNaN(numericId);
    const token = jwtToken?.jwtToken;

    useEffect(() => {
        if (!isValidId) {
            return;
        }

        dispatch(getIVRConfigurationFullById(id, token));
    }, [id, isValidId, token, dispatch]);

    useEffect(() => {
        if (!activeClick?.click || !isValidId || !token) {
            return;
        }

        if (activeClick.item !== "/ivrconfiguration") {
            return;
        }

        const fromPath = (activeClick.from ?? "").replace(/^#/, "");
        const match = fromPath.match(/^\/ivr_scheme\/(\d+)/);

        if (!match || match[1] !== String(id)) {
            return;
        }

        dispatch(getIVRConfigurationFullById(id, token));
    }, [activeClick, id, isValidId, token, dispatch]);


    useEffect(() => {
        dispatch(getAllQueues(jwtToken?.jwtToken));

    }, [dispatch, jwtToken?.jwtToken]);

    const handleGenerateChain = (nodeId: string, config: IVRBlockConfig) => {
        schemeBuilderRef.current?.generateStartConfigChain(nodeId, config);
        setSelectedNode(null);
    };

    return (
        <div className="card flex flex-column">
            {flowIdFromRoute != null && (
                <IVRSchemeNav
                    flowId={flowIdFromRoute}
                    playbackState={schemePlaybackState}
                    canPlayFromSelectedNode={
                        selectedNode != null &&
                        !isIvrEndBlockData(selectedNode.data)
                    }
                    onPlayScheme={() =>
                        schemeBuilderRef.current?.playScheme() ??
                        Promise.resolve(false)
                    }
                    onPlaySchemeFromSelected={() =>
                        selectedNode
                            ? (schemeBuilderRef.current?.playSchemeFromNode(
                                  selectedNode.id
                              ) ?? Promise.resolve(false))
                            : Promise.resolve(false)
                    }
                    onPausePlayback={() =>
                        schemeBuilderRef.current?.pauseSchemePlayback()
                    }
                    onResumePlayback={() =>
                        schemeBuilderRef.current?.resumeSchemePlayback()
                    }
                    onStopPlayback={() =>
                        schemeBuilderRef.current?.stopSchemePlayback()
                    }
                />
            )}
            <div className="flex flex-1 min-w-0">
            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
            </TabView>
            <div className="flex-1 min-w-0" style={{ minHeight: "70vh" }}>
                <IVRSchemeBuilder
                    ref={schemeBuilderRef}
                    onSelectedNodeChange={setSelectedNode}
                    onSchemePlaybackStateChange={setSchemePlaybackState}
                />
            </div>
            {selectedNode && <div
                className="ivr-settings-panel flex-shrink-0 border-left-1 border-gray-600"
            >
                <TabView
                    className="ivr-settings-tabview"
                    panelContainerClassName="ivr-settings-tabview-panels"
                    activeIndex={activeSettingIndex}
                    onTabChange={(e) => setActiveSettingIndex(e.index)}
                >
                    <TabPanel header="Налаштування" className="p-0" headerClassName="p-3">
                        <BlockSettingsPanel
                            node={selectedNode}
                            resolveLatestNode={(nodeId) =>
                                schemeBuilderRef.current?.getNodeById(nodeId)
                            }
                            getGraphElementIds={() =>
                                schemeBuilderRef.current?.collectPositiveElementIds() ??
                                []
                            }
                            getGraphEndElementIds={() =>
                                schemeBuilderRef.current?.collectEndElementPositiveIds() ??
                                []
                            }
                            resolveSchedulerJumpTargetElementId={(configNodeId) =>
                                schemeBuilderRef.current?.getSchedulerJumpTargetElementId(
                                    configNodeId
                                ) ?? null
                            }
                            flowIdFromRoute={flowIdFromRoute}
                            onDismissSettings={() => setSelectedNode(null)}
                            onSave={(nodeId, blockConfig) => {
                                schemeBuilderRef.current?.updateNodeData(
                                    nodeId,
                                    blockConfig
                                );
                                setSelectedNode((prev) =>
                                    prev && prev.id === nodeId
                                        ? {
                                              ...prev,
                                              data: {
                                                  ...prev.data,
                                                  blockConfig,
                                              },
                                          }
                                        : prev
                                );
                            }}
                            onGenerateChain={handleGenerateChain}
                        />
                    </TabPanel>
                </TabView>
            </div>}
            </div>
        </div>
    );
};

export default IVRSchemeSetting;
